import pick from 'lodash/pick';
import config from '../../config';
import { types as sdkTypes } from '../../util/sdkLoader';
import { storableError } from '../../util/errors';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { denormalisedResponseEntities } from '../../util/data';
import { TRANSITION_ENQUIRE } from '../../util/transaction';
import { reportListing, withdrawUserCredits, updateListingViewsNumber } from '../../util/api';
import { fetchCurrentUser, fetchCurrentUserHasOrdersSuccess } from '../../ducks/user.duck';
import { fetchActiveTransactions } from '../ProfilePage/ProfilePage.duck';
import { CREDITS_NUM_TO_UNLOCK_PAGE } from '../TransactionPage/TransactionPage.duck';

import {
    LISTING_PAGE_DRAFT_VARIANT,
    LISTING_PAGE_PENDING_APPROVAL_VARIANT,
} from '../../components/EditListingWizard/config';
import { insertProfileImageToUserData } from '../../util/listings';
import { showListings } from '../../ducks/SdkDataAccessLayer.duck';
import { checkIfListingIsAnonymous } from '../../util/anonym';
import { fetchLocation } from '../../ducks/locations.duck';

const { UUID } = sdkTypes;

const SIMILAR_LOCATION_LISTINGS_NUMBER = 21;

// ================ Action types ================ //
export const SET_INITAL_VALUES = 'app/ListingPage/SET_INITIAL_VALUES';

export const INTIAL_DATA_REQUEST_IN_PROGRESS = 'app/ListingPage/INTIAL_DATA_REQUEST_IN_PROGRESS';

export const SHOW_LISTING_REQUEST = 'app/ListingPage/SHOW_LISTING_REQUEST';
export const SHOW_LISTING_ERROR = 'app/ListingPage/SHOW_LISTING_ERROR';
export const SHOW_LISTINGS_SUCCESS = 'app/ListingPage/SHOW_LISTINGS_SUCCESS';

export const FETCH_REVIEWS_REQUEST = 'app/ListingPage/FETCH_REVIEWS_REQUEST';
export const FETCH_REVIEWS_SUCCESS = 'app/ListingPage/FETCH_REVIEWS_SUCCESS';
export const FETCH_REVIEWS_ERROR = 'app/ListingPage/FETCH_REVIEWS_ERROR';

export const SEND_ENQUIRY_REQUEST = 'app/ListingPage/SEND_ENQUIRY_REQUEST';
export const SEND_ENQUIRY_SUCCESS = 'app/ListingPage/SEND_ENQUIRY_SUCCESS';
export const SEND_ENQUIRY_ERROR = 'app/ListingPage/SEND_ENQUIRY_ERROR';
export const PROVIDE_SEND_ENQUIRY_MIXPANEL = 'app/ListingPage/PROVIDE_SEND_ENQUIRY_MIXPANEL';

export const SEND_COMPLAINT_REPORT_REQUEST = 'app/ListingPage/SEND_COMPLAINT_REPORT_REQUEST';
export const SEND_COMPLAINT_REPORT_SUCCESS = 'app/ListingPage/SEND_COMPLAINT_REPORT_SUCCESS';
export const SEND_COMPLAINT_REPORT_ERROR = 'app/ListingPage/SEND_COMPLAINT_REPORT_ERROR';

export const SET_LISTING_AUTHOR_DATA = 'app/ListingPage/SET_LISTING_AUTHOR_DATA';

export const FETCH_SIMILAR_DISCIPLINE_LISTINGS_REQUEST =
    'app/ListingPage/FETCH_SIMILAR_DISCIPLINE_LISTINGS_REQUEST';
export const FETCH_SIMILAR_DISCIPLINE_LISTINGS_SUCCESS =
    'app/ListingPage/FETCH_SIMILAR_DISCIPLINE_LISTINGS_SUCCESS';
export const FETCH_SIMILAR_DISCIPLINE_LISTINGS_ERROR =
    'app/ListingPage/FETCH_SIMILAR_DISCIPLINE_LISTINGS_ERROR';

// ================ Reducer ================ //

const initialState = {
    id: null,
    intialDataRequestInProgress: false,
    showListingError: null,
    showListingLoading: false,
    reviews: [],
    extReviews: [],
    fetchReviewsError: null,
    timeSlots: null,
    sendEnquiryInProgress: false,
    sendEnquiryError: null,
    sendComplaintReportInProgress: false,
    sendComplaintReportError: null,
    similarLocationListings: [],
    similarLocationError: null,
    listingAuthor: null,
};

const listingPageReducer = (state = initialState, action = {}) => {
    const { type, payload } = action;
    switch (type) {
        case INTIAL_DATA_REQUEST_IN_PROGRESS:
            return { ...state, intialDataRequestInProgress: payload };

        case SET_INITAL_VALUES:
            return { ...initialState, ...payload };

        case SHOW_LISTING_REQUEST:
            return { ...state, id: payload.id, showListingError: null, showListingLoading: true };
        case SHOW_LISTING_ERROR: {
            return { ...state, showListingError: payload, showListingLoading: false };
        }
        case SHOW_LISTINGS_SUCCESS:
            return { ...state, showListingError: null, showListingLoading: false };

        case FETCH_REVIEWS_REQUEST:
            return { ...state, fetchReviewsError: null };
        case FETCH_REVIEWS_SUCCESS:
            return { ...state, reviews: payload };
        case FETCH_REVIEWS_ERROR:
            return { ...state, fetchReviewsError: payload };

        case SEND_COMPLAINT_REPORT_REQUEST:
            return {
                ...state,
                sendComplaintReportError: null,
                sendComplaintReportInProgress: true,
            };
        case SEND_COMPLAINT_REPORT_SUCCESS:
            return {
                ...state,
                sendComplaintReportError: null,
                sendComplaintReportInProgress: false,
            };
        case SEND_COMPLAINT_REPORT_ERROR:
            return {
                ...state,
                sendComplaintReportError: payload,
                sendComplaintReportInProgress: false,
            };

        case SEND_ENQUIRY_REQUEST:
            return { ...state, sendEnquiryInProgress: true, sendEnquiryError: null };
        case SEND_ENQUIRY_SUCCESS:
            return { ...state, sendEnquiryInProgress: false };
        case SEND_ENQUIRY_ERROR:
            return { ...state, sendEnquiryInProgress: false, sendEnquiryError: payload };
        case SET_LISTING_AUTHOR_DATA:
            return { ...state, listingAuthor: payload };

        case FETCH_SIMILAR_DISCIPLINE_LISTINGS_SUCCESS:
            return { ...state, similarLocationListings: payload };
        case FETCH_SIMILAR_DISCIPLINE_LISTINGS_ERROR:
            return { ...state, similarLocationError: payload };
        case FETCH_SIMILAR_DISCIPLINE_LISTINGS_REQUEST:
            return { ...state, similarLocationError: null };
        default:
            return state;
    }
};

export default listingPageReducer;

// ================ Action creators ================ //

export const setInitialValues = initialValues => ({
    type: SET_INITAL_VALUES,
    payload: pick(initialValues, Object.keys(initialState)),
});

export const initialDataRequest = inProgress => ({
    type: INTIAL_DATA_REQUEST_IN_PROGRESS,
    payload: inProgress,
});

export const showListingRequest = id => ({
    type: SHOW_LISTING_REQUEST,
    payload: { id },
});

export const showListingError = e => ({
    type: SHOW_LISTING_ERROR,
    error: true,
    payload: e,
});

export const showListingSuccess = () => ({
    type: SHOW_LISTINGS_SUCCESS,
});

export const fetchReviewsRequest = () => ({
    type: FETCH_REVIEWS_REQUEST,
});
export const fetchReviewsSuccess = reviews => ({
    type: FETCH_REVIEWS_SUCCESS,
    payload: reviews,
});
export const fetchReviewsError = error => ({
    type: FETCH_REVIEWS_ERROR,
    error: true,
    payload: error,
});

export const fetchSimilarLocationListingsRequest = () => ({
    type: FETCH_SIMILAR_DISCIPLINE_LISTINGS_REQUEST,
});
export const fetchSimilarLocationListingsSuccess = listings => ({
    type: FETCH_SIMILAR_DISCIPLINE_LISTINGS_SUCCESS,
    payload: listings,
});
export const fetchSimilarLocationListingsError = error => ({
    type: FETCH_SIMILAR_DISCIPLINE_LISTINGS_ERROR,
    payload: error,
});

export const setListingAuthor = author => ({
    type: SET_LISTING_AUTHOR_DATA,
    payload: author,
});

export const sendEnquiryRequest = () => ({ type: SEND_ENQUIRY_REQUEST });
export const sendEnquirySuccess = txId => ({ type: SEND_ENQUIRY_SUCCESS, payload: txId });
export const sendEnquiryError = e => ({ type: SEND_ENQUIRY_ERROR, error: true, payload: e });
export const provideSendEnquiryMixpanel = payload => ({
    type: PROVIDE_SEND_ENQUIRY_MIXPANEL,
    payload,
});

export const sendComplaintReportRequest = () => ({ type: SEND_COMPLAINT_REPORT_REQUEST });
export const sendComplaintReportSuccess = () => ({ type: SEND_COMPLAINT_REPORT_SUCCESS });
export const sendComplaintReportError = e => ({
    type: SEND_COMPLAINT_REPORT_ERROR,
    error: true,
    payload: e,
});

// ================ Thunks ================ //

export const showListing = (listingId, isOwn = false) => async (dispatch, getState, sdk) => {
    const {
        ListingPage: { showListingLoading },
    } = getState();
    if (showListingLoading) return;

    dispatch(showListingRequest(listingId));
    dispatch(fetchCurrentUser());
    const params = {
        id: listingId,
        include: ['author', 'author.profileImage', 'images'],
        'fields.image': [
            // Listing page
            'variants.landscape-crop',
            'variants.landscape-crop2x',
            'variants.landscape-crop4x',
            'variants.landscape-crop6x',

            // Social media
            'variants.facebook',
            'variants.twitter',

            // Image carousel
            'variants.scaled-small',
            'variants.scaled-medium',
            'variants.scaled-large',
            'variants.scaled-xlarge',

            // Avatars
            'variants.square-small',
            'variants.square-small2x',
        ],
    };

    const show = isOwn ? sdk.ownListings.show(params) : dispatch(showListings(params));

    return show
        .then(response => {
            dispatch(showListingSuccess());
            dispatch(addMarketplaceEntities(response));

            return response;
        })
        .catch(e => {
            dispatch(showListingError(storableError(e)));
        });
};

export const fetchReviews = userId => (dispatch, getState, sdk) => {
    if (!userId) {
        return dispatch(fetchReviewsError(storableError('User id is missed.')));
    }
    dispatch(fetchReviewsRequest());
    sdk.reviews
        .query({
            subject_id: userId,
            state: 'public',
            include: ['author', 'author.profileImage'],
            'fields.image': ['variants.square-small', 'variants.square-small2x'],
        })
        .then(response => {
            const reviews = denormalisedResponseEntities(response);
            dispatch(fetchReviewsSuccess(reviews));
        })
        .catch(e => {
            dispatch(fetchReviewsError(storableError(e)));
        });
};

export const sendEnquiry = ({
    listingId,
    message,
    isSponsored,
    applyAnonymity,
    listingSubstitutionId,
}) => async (dispatch, getState, sdk) => {
    const {
        ListingPage,
        user: { currentUser },
        marketplaceData,
    } = getState();
    const { sendEnquiryInProgress } = ListingPage;

    if (sendEnquiryInProgress) return;

    dispatch(sendEnquiryRequest());

    const customerData = {
        userUUID: currentUser.id.uuid,
        name: currentUser.attributes.profile.displayName,
    };

    const {
        id: { uuid: userId },
        attributes: {
            profile: {
                publicData: { userType },
            },
        },
    } = currentUser;

    if (isSponsored) {
        /** If a tx is sponsored, the credits are withdrawn instantly */
        const response = await withdrawUserCredits(userId, CREDITS_NUM_TO_UNLOCK_PAGE, true);
        const data = await response.json();
        const { error, message, status } = data;
        if (error) {
            dispatch(
                sendEnquiryError({
                    message,
                    status,
                })
            );

            return { error, message, status };
        }
    }
    /**
     * Use listingId and not listingSubstitutionId
     * to check horse -> rider inquires a horse
     * to check rider -> horseowner inquires a rider
     */
    const listingToDeanonymize = marketplaceData.entities.listing[listingId];
    const isAnonymousListing = checkIfListingIsAnonymous(listingToDeanonymize);

    const bodyParams = {
        transition: TRANSITION_ENQUIRE,
        processAlias: config.bookingProcessAlias,
        params: {
            listingId,
            protectedData: {
                initiatedAs: userType,
                customerData: JSON.stringify(customerData),
                listingSubstitutionId,
                isSponsored,
                isAnonymousListing,
                applyAnonymity,
            },
        },
    };

    return sdk.transactions
        .initiate(bodyParams, { expand: true, include: ['customer', 'provider', 'listing'] })
        .then(async response => {
            const transactionId = response.data.data.id;

            dispatch(addMarketplaceEntities(response));

            if (message) {
                await sdk.messages.send({ transactionId, content: message.trim() });
            }

            dispatch(sendEnquirySuccess(transactionId));
            dispatch(
                provideSendEnquiryMixpanel({
                    listingId,
                    listingSubstitutionId,
                    transactionId,
                })
            );
            dispatch(fetchCurrentUserHasOrdersSuccess(true));
            /**
             * TODO
             * check if it is needed to use sendTxSGEmail to
             * send an initial message to the chat provider
             */
            return transactionId;
        })
        .catch(error => {
            dispatch(sendEnquiryError(storableError(error)));

            throw error;
        });
};

export const submitComplaintReport = body => async (dispatch, getState, sdk) => {
    dispatch(sendComplaintReportRequest());

    return reportListing(JSON.stringify(body))
        .then(res => {
            if (res.error) {
                throw new Error('Unable to send data');
            }
            dispatch(sendComplaintReportSuccess());
            return {};
        })
        .catch(error => {
            dispatch(sendComplaintReportError(error.message));
            return { error: error.message };
        });
};

export const fetchSimilarLocationListings = (params = {}) => (dispatch, getState, sdk) => {
    dispatch(fetchSimilarLocationListingsRequest());

    if (!params.origin) {
        return dispatch(fetchSimilarLocationListingsSuccess([]));
    }

    const queryParams = {
        page: 1,
        perPage: SIMILAR_LOCATION_LISTINGS_NUMBER,
        pub_type: 'horse',
        /** warning
         * the similar fields exist on the SearchPage
         * every listing\user (etc) data from request
         * is merged, and if new request is lack of some field,
         * it will be removed after through denormalisedEntities function
         */
        include: ['author', 'images'],
        'fields.listing': [
            'title',
            'geolocation',
            'price',
            'publicData',
            'metadata',
            'description',
            'createdAt',
        ],
        'fields.user': [
            'profile.displayName',
            'profile.abbreviatedName',
            'profile.bio',
            'profile.publicData',
            /**
             * fetchSimilarLocationListings is requested in the
             * ProfileSettingsForm component;
             *
             * this component also has another one dependant on
             * createdAt data, e.i. - Matching section uses this field
             * to show when a user has joined the app
             */
            'createdAt',
        ],
        'fields.image': [
            'variants.landscape-crop',
            'variants.landscape-crop2x',
            'variants.square-small',
            'variants.scaled-medium',
        ],
        ...params,
    };

    return sdk.listings
        .query(queryParams)
        .then(response => {
            const { data: listings } = response.data;

            dispatch(addMarketplaceEntities(response));
            dispatch(fetchSimilarLocationListingsSuccess(listings));
        })
        .catch(error => {
            dispatch(fetchSimilarLocationListingsError(error));
            throw error;
        });
};

export const loadData = params => async (dispatch, getState, sdk) => {
    const {
        user: { currentUser },
        ListingPage: { intialDataRequestInProgress },
    } = getState();

    if (intialDataRequestInProgress) return;

    dispatch(initialDataRequest(true));

    const currentUserId = currentUser && currentUser.id ? currentUser.id.uuid : null;
    const listingId = new UUID(params.id);
    const ownListingVariants = [LISTING_PAGE_DRAFT_VARIANT, LISTING_PAGE_PENDING_APPROVAL_VARIANT];
    const isOwnListing = ownListingVariants.includes(params.variant);
    const typeToCheck = isOwnListing ? 'currentUser' : 'user';

    const checkUser = item => item.type == typeToCheck;

    const currentListing = await dispatch(showListing(listingId, isOwnListing));
    const authorProfile = currentListing ? currentListing.data.included.filter(checkUser) : null;
    const authorId = authorProfile && authorProfile[0] ? authorProfile[0].id.uuid : null;
    const isOwnerView = currentUserId && currentUserId === authorId;

    if (authorProfile && authorProfile.length) {
        dispatch(setListingAuthor(insertProfileImageToUserData(authorProfile[0], getState())));
    }

    const { country, addressDetails } = currentListing?.data?.data?.attributes?.publicData || {};
    const listingLocationRequest = dispatch(
        fetchLocation(country || addressDetails?.country, addressDetails?.postalCode)
    );

    try {
        if (!isOwnerView) {
            const publicData = currentListing?.data?.data?.attributes?.publicData;

            if (publicData) {
                const { viewsNum_total, viewsNum_temp } = publicData;

                const query = new URLSearchParams({
                    total: viewsNum_total || 0,
                    temp: viewsNum_temp || 0,
                });
                await updateListingViewsNumber(params.id, query);
            }
        }
    } catch (e) {
        // do nothing
    }

    if (config.enableAvailability) {
        return Promise.all([
            // dispatch(showListing(listingId)), add isOwnListing
            // dispatch(fetchTimeSlots(listingId)),
            dispatch(fetchReviews(authorId)),
            dispatch(fetchActiveTransactions()),
            dispatch(initialDataRequest(false)),
            listingLocationRequest,
        ]);
    } else {
        return Promise.all([
            // dispatch(showListing(listingId)), add isOwnListing
            dispatch(fetchReviews(authorId)),
            dispatch(fetchActiveTransactions()),
            dispatch(initialDataRequest(false)),
            listingLocationRequest,
        ]);
    }
};
